/**
 * @file finance-sales-report.tsx
 * @module app/modules/finance-reports/finance-sales-report
 * @description Displays the finance sales report with date filters, sales totals, and progress data.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { Card, Col, FormLabel, OverlayTrigger, Popover, Row, ProgressBar } from "react-bootstrap";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  CartonWithDozens,
  CartonWithPieces,
  Dozen,
  FinanceSalesReportPermission,
  PAGE_LIMIT_LARGE,
  Piece,
  SellersSalesReport,
  OutWardReports,
  Download,
  RouteOrderMovedToPendingOrder,
} from "../../../utils/constants";
import APICallService from "../../../api/apiCallService";
import { seller } from "../../../api/apiEndPoints";
import Method from "../../../utils/methods";
import Loader from "../../../Global/loader";
import CustomDatePicker from "../../custom/DateRange/DatePicker";
import { InfiniteScroll } from "../../components";
import { useAuth } from "../auth";
import clsx from "clsx";
import { CustomSelect } from "../../custom/Select/CustomSelect";
import { useWarehouse } from "../../context/WarehouseContext";
import {
  buildFinanceSalesReportParams,
  financeSalesReportCacheKey,
  normalizeTimeToSeconds,
  FinanceReportResponseCache,
} from "./finance-sales-report-cache";
import React from "react";
import { FinanceNetAmount, FinanceSalesAmounts } from "./finance-report-metrics";
import ImageWithFallback from "../_common/components/image-with-fallback";

const currentEastAfricaDate = (): string => {
  const parts = new Intl.DateTimeFormat("en", {
    timeZone: "Africa/Dar_es_Salaam",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const value = (type: string) => parts.find((part) => part.type === type)?.value || "";
  return `${value("year")}-${value("month")}-${value("day")}`;
};

export type FinanceSalesReportProps = {
  reportTitle?: string;
  viewModeStorageKey?: string;
  exportSheetName?: string;
  exportFileNamePrefix?: string;
  hideDeliveredQty?: boolean;
  hideReturnedQty?: boolean;
  /** When true, API includes orders once marked Dispatched (even if cancelled/delivered/returned). */
  isOutward?: boolean;
};

const FinanceSalesReport = ({
  reportTitle = "Sales Report",
  viewModeStorageKey = "salesReport-viewMode",
  exportSheetName = "Sales Report",
  exportFileNamePrefix = "Finance-Sales-Report",
  hideDeliveredQty = false,
  hideReturnedQty = false,
  isOutward = false,
}: FinanceSalesReportProps) => {
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const navigate = useNavigate();
  const { state }: any = useLocation();
  const [details, setDetails] = useState<any>();
  const [endDate, setEndDate] = useState<any>(new Date());
  const [startDate, setStartDate] = useState<any>(new Date());
  const [outwardStartAt, setOutwardStartAt] = useState(() =>
    `${currentEastAfricaDate()}T00:00:00`,
  );
  const [outwardEndAt, setOutwardEndAt] = useState(() =>
    `${currentEastAfricaDate()}T23:59:59`,
  );
  const [appliedOutwardFilters, setAppliedOutwardFilters] = useState<any>(null);
  const [appliedStandardFilters, setAppliedStandardFilters] = useState<any>(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [page, setPage] = useState(1);
  const pageLimit = PAGE_LIMIT_LARGE;
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = React.useRef(false);
  const [search, setSearch] = useState<string>("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [paymentData, setPaymentData] = useState<any>([]);
  // Range-scoped aggregates returned by the API. Summary totals and the
  // consolidated rollup MUST come from the server: computing them from
  // paymentData would scope them to the current page and report wrong figures.
  const [summaryTotals, setSummaryTotals] = useState<any>(null);
  const [serverConsolidated, setServerConsolidated] = useState<any>([]);
  const [paymentDetails, setPaymentDetails] = useState<any>([]);
  const [productLoading, setProductLoading] = useState(false);
  const [searchProduct, setSearchProduct] = useState("");
  const [totalProducts, setTotalProducts] = useState(0);
  const [selectedBrand, setSelectedBrand] = useState<any>();
  const [selectedDistrict, setSelectedDistrict] = useState<any>();
  const [selectedProduct, setSelectedProduct] = useState<any>();
  const [selectedDriver, setSelectedDriver] = useState<any>(null);
  const [productsList, setProductsList] = useState<any>([]);
  const [driversList, setDriversList] = useState<any[]>([]);
  const [brands, setBrands] = useState<any>([]);
  const [district, setDistricts] = useState<any>([]);
  const [isExporting, setIsExporting] = useState(false);
  const savedData = localStorage.getItem(viewModeStorageKey) as
    | "normal"
    | "consolidated";
  const [viewMode, setViewMode] = useState<"normal" | "consolidated">(
    savedData ?? "normal",
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);
  const exportRef = React.useRef<number>(0);
  const exportCacheRef = React.useRef<{ filterKey: string; data: any[] } | null>(null);
  const fetchRef = React.useRef<number>(0);
  const responseCache = React.useRef(new FinanceReportResponseCache());
  useEffect(() => { responseCache.current.clear(); }, [currentUser]);
  const isAllowedToDownload = Method.hasPermission(
    FinanceSalesReportPermission,
    Download,
    currentUser,
  );
  const outwardDraftFilters = {
    fromDate: outwardStartAt.slice(0, 10),
    toDate: outwardEndAt.slice(0, 10),
    fromTime: normalizeTimeToSeconds(outwardStartAt.slice(11), "00:00:00"),
    toTime: normalizeTimeToSeconds(outwardEndAt.slice(11), "23:59:59"),
    brand: selectedBrand?.id || selectedBrand?._id || "",
    product: selectedProduct?.value || "",
    district: selectedDistrict?.value || selectedDistrict?.id || "",
    driver: selectedDriver?.value || "",
  };
  const standardDraftFilters = {
    fromDate: startDate ? Method.convertDateToFormat(startDate, "YYYY-MM-DD") : "",
    toDate: endDate ? Method.convertDateToFormat(endDate, "YYYY-MM-DD") : "",
    brand: selectedBrand?.id || selectedBrand?._id || "",
    product: selectedProduct?.value || "",
    district: selectedDistrict?.value || selectedDistrict?.id || "",
    driver: selectedDriver?.value || "",
  };
  const outwardFiltersPending = isOutward && (
    !appliedOutwardFilters ||
    JSON.stringify(outwardDraftFilters) !== JSON.stringify(appliedOutwardFilters)
  );
  const standardFiltersPending = !isOutward && (
    !appliedStandardFilters ||
    JSON.stringify(standardDraftFilters) !== JSON.stringify(appliedStandardFilters)
  );
  const reportFiltersPending = outwardFiltersPending || standardFiltersPending;
  const standardDateRangeValid = Boolean(startDate && endDate) &&
    new Date(startDate).getTime() <= new Date(endDate).getTime();
  const outwardRangeValid =
    new Date(`${outwardDraftFilters.fromDate}T${outwardDraftFilters.fromTime}`).getTime() <=
    new Date(`${outwardDraftFilters.toDate}T${outwardDraftFilters.toTime}`).getTime();

  useEffect(() => {
    localStorage.setItem(viewModeStorageKey, viewMode);
  }, [viewMode, viewModeStorageKey]);

  useEffect(() => {
    (async () => {
      setFetchLoading(true);
      if (
        !Method.hasModulePermission(SellersSalesReport, currentUser) &&
        !Method.hasModulePermission(OutWardReports, currentUser)
      ) {
        return window.history.back();
      }
      setSelectedBrand(null);
      setSelectedProduct(null);
      setSelectedDistrict(null);
      setSelectedDriver(null);
      setDriversList([]);
      setPage(1);
      if (isOutward) {
        const initialFilters = {
          fromDate: outwardStartAt.slice(0, 10),
          toDate: outwardEndAt.slice(0, 10),
          fromTime: outwardDraftFilters.fromTime,
          toTime: outwardDraftFilters.toTime,
          brand: "",
          product: "",
          district: "",
          driver: "",
        };
        setAppliedOutwardFilters(initialFilters);
        await fetchOrder(
          initialFilters.fromDate,
          initialFilters.toDate,
          null,
          null,
          null,
          null,
          1,
          pageLimit,
          initialFilters.fromTime,
          initialFilters.toTime,
        );
      } else {
        const initialFilters = {
          fromDate: Method.convertDateToFormat(startDate, "YYYY-MM-DD"),
          toDate: Method.convertDateToFormat(endDate, "YYYY-MM-DD"),
          brand: "",
          product: "",
          district: "",
          driver: "",
        };
        setAppliedStandardFilters(initialFilters);
        await fetchOrder(
          initialFilters.fromDate,
          initialFilters.toDate,
          null,
          null,
          null,
          null,
        );
      }
      setFetchLoading(false);
    })();
  }, [selectedWarehouse?.value]);

  const fetchOrder = async (
    startDate: string,
    endDate: string,
    brands?: any,
    product?: any,
    district?: any,
    driver: any = null,
    pageNo: number = 1,
    limit: number = pageLimit,
    fromTime: string = outwardStartAt.split("T")[1] || "00:00:00",
    toTime: string = outwardEndAt.split("T")[1] || "23:59:59",
    append: boolean = false,
  ) => {
    const runId = ++fetchRef.current;
    const params = buildFinanceSalesReportParams({
      fromDate: startDate
        ? Method.convertDateToFormat(startDate, "YYYY-MM-DD")
        : "",
      toDate: endDate ? Method.convertDateToFormat(endDate, "YYYY-MM-DD") : "",
      brand: brands,
      product,
      district,
      driver,
      warehouse: selectedWarehouse?.value || null,
      isOutward,
      fromTime,
      toTime,
      pagination: true,
      limit,
      skip: (pageNo - 1) * limit,
    });
    let apiService = new APICallService(
      seller.salesReport,
      params,
      "",
      "",
      "",
      "",
      FinanceSalesReportPermission,
      selectedWarehouse?.value,
    );
    const response = await responseCache.current.load(
      JSON.stringify(params),
      () => apiService.callAPI(),
      (result) => Array.isArray(result) || (Array.isArray(result?.records) && Array.isArray(result?.consolidated) && Boolean(result?.summary)),
    );
    if (runId !== fetchRef.current) return false;
    if (response) {
      const isPaginated = !Array.isArray(response);
      const records = isPaginated ? response?.records ?? [] : response;

      setPaymentData((previous: any[]) =>
        append ? [...previous, ...records] : records,
      );
      setTotalRecords(isPaginated ? response?.total ?? 0 : records.length);
      setSummaryTotals(isPaginated ? response?.summary ?? null : null);
      setServerConsolidated(isPaginated ? response?.consolidated ?? [] : []);
      if (driver === null && isPaginated) {
        setDriversList(response?.driverOptions ?? []);
      }

      if (!product) {
        const optionSource = isPaginated ? response?.consolidated ?? [] : records;
        getUniqueProducts(optionSource);
        getUniqueBrands(optionSource);
        getUniqueDistricts(optionSource);
      }
    }
    return Boolean(response);
  };

  const clearSummaryState = () => {
    setSummaryTotals(null);
    setServerConsolidated([]);
  };

  const getUniqueProducts = (data: any) => {
    const uniqueVariants = new Set();
    data.forEach((item: any) => {
      const variant = item?.variant;
      const variantInfo = {
        title: variant?.title,
        id: variant?._id,
        media: variant?.media,
      };
      uniqueVariants?.add(JSON.stringify(variantInfo));
    });
    const uniqueVariantsArray = Array?.from(uniqueVariants)?.map(
      (variant: any) => JSON.parse(variant),
    );
    setProductsList(uniqueVariantsArray);
  };

  const getUniqueBrands = (data: any) => {
    const uniqueVariants = new Set();
    data.forEach((item: any) => {
      const variant = item?.variant?.product?.brand;
      const variantInfo = {
        title: variant?.title,
        id: variant?.reference,
        media: variant?.media,
      };
      uniqueVariants?.add(JSON.stringify(variantInfo));
    });
    const uniqueVariantsArray = Array?.from(uniqueVariants)?.map(
      (variant: any) => JSON.parse(variant),
    );
    setBrands(uniqueVariantsArray);
  };

  const getUniqueDistricts = (data: any) => {
    const uniqueVariants = new Set();
    data?.forEach((item: any) => {
      const variant = item?.district;
      const variantInfo = {
        title: variant?.name,
        id: variant?.id,
      };
      uniqueVariants?.add(JSON.stringify(variantInfo));
    });
    const uniqueVariantsArray = Array?.from(uniqueVariants)?.map(
      (variant: any) => JSON.parse(variant),
    );
    setDistricts(uniqueVariantsArray);
  };

  const handleChange = async ([startDate, endDate]: any) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const handleProductSelect = (event: any) => {
    setSelectedProduct(event || null);
  };

  const onMenuClose = async () => {
    setSearch("");
  };
  const handleDriverSelect = (event: any) => {
    setSelectedDriver(event || null);
  };

  const handleOutwardDateTimeChange = async (
    boundary: "from" | "to",
    value: string,
  ) => {
    if (!value) return;
    if (boundary === "from") setOutwardStartAt(value);
    else setOutwardEndAt(value);
  };

  const handleApplyOutwardFilters = async () => {
    if (!isOutward) return;
    const filters = { ...outwardDraftFilters };
    const fromDate = new Date(`${filters.fromDate}T${filters.fromTime}`);
    const toDate = new Date(`${filters.toDate}T${filters.toTime}`);
    if (!Number.isFinite(fromDate.getTime()) || !Number.isFinite(toDate.getTime()) || fromDate > toDate) return;

    setAppliedOutwardFilters(filters);
    setTotalRecords(0);
    setPaymentData([]);
    clearSummaryState();
    setFetchLoading(true);
    setPage(1);
    await fetchOrder(
      filters.fromDate,
      filters.toDate,
      filters.brand || null,
      filters.product || null,
      filters.district || null,
      filters.driver || null,
      1,
      pageLimit,
      filters.fromTime,
      filters.toTime,
    );
    setFetchLoading(false);
  };

  const handleApplyStandardFilters = async () => {
    if (isOutward || !standardDateRangeValid) return;
    const filters = { ...standardDraftFilters };
    setAppliedStandardFilters(filters);
    setTotalRecords(0);
    setPaymentData([]);
    clearSummaryState();
    setFetchLoading(true);
    setPage(1);
    try {
      await fetchOrder(
        filters.fromDate,
        filters.toDate,
        filters.brand || null,
        filters.product || null,
        filters.district || null,
        filters.driver || null,
        1,
        pageLimit,
      );
    } finally {
      setFetchLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (
      fetchLoading ||
      loadingMoreRef.current ||
      viewMode !== "normal" ||
      reportFiltersPending ||
      paymentData.length >= totalRecords
    ) return;

    const filters = isOutward ? appliedOutwardFilters : appliedStandardFilters;
    if (!filters) return;

    loadingMoreRef.current = true;
    setLoadingMore(true);
    const nextPage = page + 1;
    try {
      const loaded = await fetchOrder(
        filters.fromDate,
        filters.toDate,
        filters.brand || null,
        filters.product || null,
        filters.district || null,
        filters.driver || null,
        nextPage,
        pageLimit,
        filters?.fromTime,
        filters?.toTime,
        true,
      );
      if (loaded) setPage(nextPage);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  const getQuantityTypeLabel = (quantityType: any) => {
    if (quantityType === CartonWithDozens) return "CWD";
    if (quantityType === CartonWithPieces) return "CWP";
    if (quantityType === Dozen) return "D";
    if (quantityType === Piece) return "P";
    return "Unknown";
  };

  const toggleSort = () => {
    if (sortOrder === null) {
      setSortOrder("desc");
    } else if (sortOrder === "desc") {
      setSortOrder("asc");
    } else {
      setSortOrder(null);
    }
  };

  // Keyed by `${variantId}-${quantityType}`. A Map lookup replaces a linear
  // acc.find() scan, which made this O(n^2) on large reports.
  const consolidateReportData = (data: any[]) => {
    const grouped = new Map<string, any>();

    data.forEach((val: any) => {
      const variantId = val?.variant?._id;
      const quantityType = val?.quantityType;
      const key = `${variantId}-${quantityType}`;

      const individualDeliveredQty =
        val?.deliveredQuantities !== undefined
          ? val?.deliveredQuantities
          : (val?.loadedQuantities || 0) - (val?.totalReturnedQuantities || 0);

      const existing = grouped.get(key);

      if (existing) {
        existing.totalQuantities =
          (existing.totalQuantities || 0) + (val?.totalQuantities || 0);
        existing.loadedQuantities =
          (existing.loadedQuantities || 0) + (val?.loadedQuantities || 0);
        existing.totalReturnedQuantities =
          (existing.totalReturnedQuantities || 0) +
          (val?.totalReturnedQuantities || 0);
        existing.deliveredQuantities =
          (existing.deliveredQuantities || 0) + individualDeliveredQty;
        existing.totalDelivered =
          (existing.totalDelivered || 0) + individualDeliveredQty;

        existing.loadedAmount =
          (existing.loadedAmount || 0) +
          (val?.loadedAmount ?? (val?.loadedQuantities || 0) * (val?.averagePrice || 0));
        existing.loadedTaxAmount =
          (existing.loadedTaxAmount || 0) +
          (val?.loadedTaxAmount ?? (val?.loadedQuantities || 0) * (val?.averageTaxPrice || 0));
        existing.loadedVAT =
          (existing.loadedVAT || 0) +
          (val?.loadedVAT ?? (val?.loadedQuantities || 0) * (val?.taxPerUnit || 0));

        existing.deliveredAmount =
          (existing.deliveredAmount || 0) +
          (val?.deliveredAmount ?? (val?.totalAmount || 0));
        existing.deliveredTaxAmount =
          (existing.deliveredTaxAmount || 0) +
          (val?.deliveredTaxAmount ?? (val?.totalTaxAmount || 0));
        existing.deliveredVAT =
          (existing.deliveredVAT || 0) +
          (val?.deliveredVAT ?? (val?.tax || 0));

        existing.returnedAmount =
          (existing.returnedAmount || 0) +
          (val?.returnedAmount ?? (val?.totalReturnedQuantities || 0) * (val?.averagePrice || 0));
        existing.returnedTaxAmount =
          (existing.returnedTaxAmount || 0) +
          (val?.returnedTaxAmount ?? (val?.totalReturnedQuantities || 0) * (val?.averageTaxPrice || 0));
        existing.returnedVAT =
          (existing.returnedVAT || 0) +
          (val?.returnedVAT ?? (val?.totalReturnedQuantities || 0) * (val?.taxPerUnit || 0));

        existing.totalAmount =
          (existing.totalAmount || 0) + (val?.totalAmount || 0);
        existing.totalTaxAmount =
          (existing.totalTaxAmount || 0) + (val?.totalTaxAmount || 0);
        existing.tax = (existing.tax || 0) + (val?.tax || 0);
        existing.hasMovedToPending =
          existing.hasMovedToPending ||
          (isOutward &&
            val?.routeVehicleStatus === RouteOrderMovedToPendingOrder);
        existing.orderCount = (existing.orderCount || 1) + 1;
      } else {
        grouped.set(key, {
          ...val,
          loadedAmount:
            val?.loadedAmount ?? (val?.loadedQuantities || 0) * (val?.averagePrice || 0),
          loadedTaxAmount:
            val?.loadedTaxAmount ?? (val?.loadedQuantities || 0) * (val?.averageTaxPrice || 0),
          loadedVAT:
            val?.loadedVAT ?? (val?.loadedQuantities || 0) * (val?.taxPerUnit || 0),
          deliveredAmount: val?.deliveredAmount ?? val?.totalAmount ?? 0,
          deliveredTaxAmount: val?.deliveredTaxAmount ?? val?.totalTaxAmount ?? 0,
          deliveredVAT: val?.deliveredVAT ?? val?.tax ?? 0,
          returnedAmount:
            val?.returnedAmount ?? (val?.totalReturnedQuantities || 0) * (val?.averagePrice || 0),
          returnedTaxAmount:
            val?.returnedTaxAmount ?? (val?.totalReturnedQuantities || 0) * (val?.averageTaxPrice || 0),
          returnedVAT:
            val?.returnedVAT ?? (val?.totalReturnedQuantities || 0) * (val?.taxPerUnit || 0),
          hasMovedToPending: Boolean(
            isOutward &&
              (val?.hasMovedToPending ||
                val?.routeVehicleStatus === RouteOrderMovedToPendingOrder),
          ),
          deliveredQuantities: individualDeliveredQty,
          totalDelivered: individualDeliveredQty,
          orderCount: 1,
        });
      }
    });

    // Map preserves insertion order, matching the previous acc.push() ordering.
    // Calculate Volume-Weighted Average Price (VWAP) compliant with SAP/IFRS.
    return Array.from(grouped.values()).map((item: any) => {
      const loadQty = item.loadedQuantities || 0;
      let avgPrice = item.averagePrice || 0;
      let avgTaxPrice = item.averageTaxPrice || 0;

      if (loadQty > 0) {
        avgPrice = Number(((item.loadedAmount || 0) / loadQty).toFixed(2));
        avgTaxPrice = Number(((item.loadedTaxAmount || 0) / loadQty).toFixed(2));
      }

      return {
        ...item,
        averagePrice: avgPrice,
        averageTaxPrice: avgTaxPrice,
        taxPerUnit: Number((avgPrice - avgTaxPrice).toFixed(2)),
      };
    });
  };

  const consolidatedData = useMemo(() => {
    if (viewMode !== "consolidated") return [];
    return serverConsolidated.length
      ? serverConsolidated
      : consolidateReportData(paymentData);
  }, [paymentData, serverConsolidated, viewMode]);

  const displayData = useMemo(
    () => (viewMode === "consolidated" ? consolidatedData : paymentData),
    [viewMode, consolidatedData, paymentData],
  );

  const sortedDisplayData = useMemo(() => {
    if (sortOrder !== "asc" && sortOrder !== "desc") {
      return displayData;
    }
    return [...displayData].sort((a: any, b: any) => {
      const qtyA = a?.totalQuantities || 0;
      const qtyB = b?.totalQuantities || 0;
      return sortOrder === "asc" ? qtyA - qtyB : qtyB - qtyA;
    });
  }, [displayData, sortOrder]);

  const summaryBase = serverConsolidated.length
    ? serverConsolidated
    : paymentData;

  const clientTotal = (fn: (val: any) => number) =>
    summaryBase.reduce((acc: number, val: any) => acc + fn(val), 0);

  const totalLoadedInclVAT =
    summaryTotals?.totalLoadedInclVAT ??
    clientTotal((val) => val?.loadedAmount ?? (val?.loadedQuantities || 0) * (val?.averagePrice || 0));
  const totalLoadedExclVAT =
    summaryTotals?.totalLoadedExclVAT ??
    clientTotal((val) => val?.loadedTaxAmount ?? (val?.loadedQuantities || 0) * (val?.averageTaxPrice || 0));
  const totalLoadedVAT =
    summaryTotals?.totalLoadedVAT ??
    clientTotal((val) => val?.loadedVAT ?? (val?.loadedQuantities || 0) * (val?.taxPerUnit || 0));

  const totalReturnedInclVAT =
    summaryTotals?.totalReturnedInclVAT ??
    clientTotal((val) => val?.returnedAmount ?? (val?.totalReturnedQuantities || 0) * (val?.averagePrice || 0));
  const totalReturnedExclVAT =
    summaryTotals?.totalReturnedExclVAT ??
    clientTotal((val) => val?.returnedTaxAmount ?? (val?.totalReturnedQuantities || 0) * (val?.averageTaxPrice || 0));
  const totalReturnedVAT =
    summaryTotals?.totalReturnedVAT ??
    clientTotal((val) => val?.returnedVAT ?? (val?.totalReturnedQuantities || 0) * (val?.taxPerUnit || 0));

  const totalLoadedQty =
    summaryTotals?.totalLoadedQty ??
    clientTotal((val) => val?.loadedQuantities || 0);

  const totalReturnedQty =
    summaryTotals?.totalReturnedQty ??
    clientTotal((val) => val?.totalReturnedQuantities || 0);

  const loadedQtyByType = summaryBase.reduce(
    (acc: any, val: any) => {
      const qty = val?.loadedQuantities || 0;
      if (val?.quantityType === CartonWithDozens) {
        acc.cartonWithDozens += qty;
      } else if (val?.quantityType === CartonWithPieces) {
        acc.cartonWithPieces += qty;
      } else if (val?.quantityType === Dozen) {
        acc.dozen += qty;
      } else if (val?.quantityType === Piece) {
        acc.piece += qty;
      }
      return acc;
    },
    { cartonWithDozens: 0, cartonWithPieces: 0, dozen: 0, piece: 0 },
  );

  const returnedQtyByType = summaryBase.reduce(
    (acc: any, val: any) => {
      const qty = val?.totalReturnedQuantities || 0;
      if (val?.quantityType === CartonWithDozens) {
        acc.cartonWithDozens += qty;
      } else if (val?.quantityType === CartonWithPieces) {
        acc.cartonWithPieces += qty;
      } else if (val?.quantityType === Dozen) {
        acc.dozen += qty;
      } else if (val?.quantityType === Piece) {
        acc.piece += qty;
      }
      return acc;
    },
    { cartonWithDozens: 0, cartonWithPieces: 0, dozen: 0, piece: 0 },
  );

  const netInclVAT = FinanceNetAmount(totalLoadedInclVAT, totalReturnedInclVAT);
  const netExclVAT = FinanceNetAmount(totalLoadedExclVAT, totalReturnedExclVAT);
  const netVAT = FinanceNetAmount(totalLoadedVAT, totalReturnedVAT);

  const LOADING_PLACEHOLDER = "--";

  const summaryAmount = (value: number) =>
    fetchLoading ? (
      <span className="text-gray-400">{LOADING_PLACEHOLDER}</span>
    ) : (
      `TSh ${Method.getGeneralizedAmount(value)}`
    );

  const summaryQty = (qtyByType: any) => {
    if (fetchLoading)
      return <span className="text-gray-400">{LOADING_PLACEHOLDER}</span>;
    const parts: string[] = [];
    if (qtyByType.cartonWithDozens > 0)
      parts.push(`${qtyByType.cartonWithDozens}CWD`);
    if (qtyByType.cartonWithPieces > 0)
      parts.push(`${qtyByType.cartonWithPieces}CWP`);
    if (qtyByType.dozen > 0) parts.push(`${qtyByType.dozen}D`);
    if (qtyByType.piece > 0) parts.push(`${qtyByType.piece}P`);
    return parts.length ? parts.join(" ") : "0";
  };

  const deliveredOrOutwardLabel = hideDeliveredQty ? "Outward" : "Delivered";
  const loadedTotalHeaders = [
    `${isOutward ? "Outward" : "Gross Sales"} (Incl. VAT)`,
    `${isOutward ? "Outward" : "Gross Sales"} (Excl. VAT)`,
    `${isOutward ? "Outward" : "Gross Sales"} VAT Amount`,
  ];
  const deliveredOrOutwardTotalHeaders = [
    `${deliveredOrOutwardLabel} Total (Incl. VAT)`,
    `${deliveredOrOutwardLabel} Total (Excl. VAT)`,
    `${deliveredOrOutwardLabel} VAT Amount`,
  ];
  const returnedTotalHeaders = [
    `${isOutward ? "In-window Returned" : "Returned"} Total (Incl. VAT)`,
    `${isOutward ? "In-window Returned" : "Returned"} Total (Excl. VAT)`,
    `${isOutward ? "In-window Returned" : "Returned"} VAT Amount`,
  ];

  const exportHeaders = isOutward ? [
    "Product Name",
    "Reference Key",
    "Customer Name",
    "Unit Price (Excl. VAT)",
    "VAT / Unit",
    "Ordered Quantity",
    "Outward Quantity",
    "Outward Total (Excl. VAT)",
    "Outward VAT Amount",
    "Outward Total (Incl. VAT)",
    "Route Status Note",
  ] : [
    "Product Name",
    "Reference Key",
    "Customer Name",
    "Unit Price (Incl. VAT)",
    "Unit Price (Excl. VAT)",
    "VAT / Unit",
    hideDeliveredQty ? "Ordered Quantity" : "Total Quantity",
    hideDeliveredQty ? "Outward Quantity" : "Loaded Quantity",
    ...(!hideReturnedQty ? [isOutward ? "In-window Returned Quantity" : "Returned Quantity"] : []),
    ...(!hideDeliveredQty ? ["Delivered Quantity"] : []),
    ...(hideDeliveredQty
      ? deliveredOrOutwardTotalHeaders
      : loadedTotalHeaders),
    ...(!hideReturnedQty ? returnedTotalHeaders : []),
    `${isOutward ? "Net After In-window Returns" : "Net Sales"} (Incl. VAT)`,
    `${isOutward ? "Net After In-window Returns" : "Net Sales"} (Excl. VAT)`,
    "Net VAT Amount",
  ];

  const rowMapper = (val: any, isConsolidated: boolean) => {
    const amounts = FinanceSalesAmounts(val);
    const totalQty = val?.totalQuantities || 0;
    const loadedQty = val?.loadedQuantities || 0;
    const returnedQty = val?.totalReturnedQuantities || 0;
    const deliveredQty =
      val?.deliveredQuantities !== undefined
        ? val?.deliveredQuantities
        : (val?.loadedQuantities || 0) - (val?.totalReturnedQuantities || 0);
    const loadedTotalIncl = amounts.loadedIncl;
    const loadedTotalExcl = amounts.loadedExcl;
    const loadedVAT = amounts.loadedVat;
    const loadedTotals = [loadedTotalIncl, loadedTotalExcl, loadedVAT];

    const returnedTotalIncl = amounts.returnedIncl;
    const returnedTotalExcl = amounts.returnedExcl;
    const returnedVAT = amounts.returnedVat;

    const netIncl = amounts.netIncl;
    const netExcl = amounts.netExcl;
    const netVAT = amounts.netVat;
    const hasMovedToPending =
      isOutward &&
      (val?.hasMovedToPending === true ||
        val?.routeVehicleStatus === RouteOrderMovedToPendingOrder);

    if (isOutward) {
      return [
        val?.variant?.title || "N/A",
        ...(isConsolidated ? [] : [val?.refKey || "N/A", val?.customerName || "N/A"]),
        val?.averageTaxPrice || 0,
        val?.taxPerUnit || 0,
        totalQty,
        loadedQty,
        amounts.loadedExcl,
        amounts.loadedVat,
        amounts.loadedIncl,
        hasMovedToPending ? "Moved to pending after dispatch" : "",
      ];
    }

    return [
      val?.variant?.title || "N/A",
      ...(isConsolidated
        ? []
        : [val?.refKey || "N/A", val?.customerName || "N/A"]),
      val?.averagePrice || 0,
      val?.averageTaxPrice || 0,
      val?.taxPerUnit || 0,
      totalQty,
      loadedQty,
      ...(!hideReturnedQty ? [returnedQty] : []),
      ...(!hideDeliveredQty ? [deliveredQty] : []),
      ...loadedTotals,
      ...(!hideReturnedQty
        ? [returnedTotalIncl, returnedTotalExcl, returnedVAT]
        : []),
      netIncl,
      netExcl,
      netVAT,
    ];
  };

  const appliedExportFilters = isOutward
    ? appliedOutwardFilters
    : appliedStandardFilters;
  const exportParams = buildFinanceSalesReportParams({
    fromDate: appliedExportFilters?.fromDate || standardDraftFilters.fromDate,
    toDate: appliedExportFilters?.toDate || standardDraftFilters.toDate,
    brand: appliedExportFilters?.brand || null,
    product: appliedExportFilters?.product || null,
    district: appliedExportFilters?.district || null,
    driver: appliedExportFilters?.driver || null,
    warehouse: selectedWarehouse?.value || null,
    isOutward,
    fromTime: isOutward ? appliedExportFilters?.fromTime : undefined,
    toTime: isOutward ? appliedExportFilters?.toTime : undefined,
  });

  const handleExportExcel = async () => {
    const runId = ++exportRef.current;
    const isConsolidated = viewMode === "consolidated";

    const currentFilterKey = financeSalesReportCacheKey({
      fromDate: exportParams.fromDate,
      toDate: exportParams.toDate,
      warehouse: selectedWarehouse?.value || "",
      isOutward: Boolean(isOutward),
      product: exportParams["variants[0]"] || "",
      brand: exportParams["brands[0]"] || "",
      district: exportParams["districts[0]"] || "",
      driver: exportParams.driver || "",
      fromTime: exportParams.fromTime || "",
      toTime: exportParams.toTime || "",
    });

    const exportDataToFile = (dataToExport: any[], toastId?: any) => {
      const isConsolidatedMode = viewMode === "consolidated";
      const finalData = isConsolidatedMode
        ? (dataToExport === serverConsolidated ? dataToExport : consolidateReportData(dataToExport))
        : dataToExport;

      const headers = isConsolidatedMode
        ? exportHeaders.filter(
            (header) => !["Reference Key", "Customer Name"].includes(header),
          )
        : exportHeaders;

      const workbook = XLSX.utils.book_new();
      const worksheetData: any[] = [headers];

      if (toastId) {
        toast.update(toastId, {
          render: `Mapping ${finalData.length} records...`,
          type: "info",
          isLoading: true,
        });
      }

      worksheetData.push(
        ...finalData.map((val: any) => rowMapper(val, isConsolidatedMode)),
      );

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      worksheet["!cols"] = isOutward ? [
        { width: 35 },
        { width: 15 },
        { width: 20 },
        { width: 20 },
        { width: 15 },
        { width: 15 },
        { width: 15 },
        { width: 20 },
        { width: 15 },
        { width: 20 },
        { width: 34 },
      ] : [
        { width: 35 },
        { width: 15 },
        { width: 20 },
        { width: 20 },
        { width: 20 },
        { width: 15 },
        { width: 15 },
        { width: 15 },
        { width: 15 },
        { width: 15 },
        { width: 20 },
        { width: 20 },
        { width: 15 },
        { width: 20 },
        { width: 20 },
        { width: 15 },
        { width: 20 },
        { width: 20 },
        { width: 15 },
        { width: 20 },
        { width: 20 },
        { width: 15 },
      ];
      if (isConsolidatedMode) {
        worksheet["!cols"].splice(1, 2);
      }
      XLSX.utils.book_append_sheet(workbook, worksheet, exportSheetName);
      XLSX.writeFile(
        workbook,
        `${exportFileNamePrefix}-${new Date().toISOString().split("T")[0]}.xlsx`,
      );
      if (toastId) {
        toast.update(toastId, {
          render: "Export completed successfully!",
          type: "success",
          isLoading: false,
          autoClose: 3000,
          closeButton: true,
          progress: undefined,
        });
      } else {
        toast.success("Export completed successfully!");
      }
    };

    if (isConsolidated && serverConsolidated && serverConsolidated.length > 0) {
      exportDataToFile(serverConsolidated);
      return;
    }

    if (paymentData && paymentData.length >= totalRecords && totalRecords > 0) {
      exportDataToFile(paymentData);
      return;
    }

    if (
      exportCacheRef.current &&
      exportCacheRef.current.filterKey === currentFilterKey &&
      exportCacheRef.current.data?.length > 0
    ) {
      exportDataToFile(exportCacheRef.current.data);
      return;
    }

    // Tier 4: Fetch once only if full dataset is not yet in memory
    const toastId = toast.loading("Fetching all records...");
    const sanitizedParams: any = { ...exportParams, download: true };

    try {
      setIsExporting(true);
      if (runId !== exportRef.current) {
        toast.dismiss(toastId);
        return;
      }

      const apiService = new APICallService(
        seller.salesReport,
        sanitizedParams,
        "",
        "",
        "",
        "",
        FinanceSalesReportPermission,
        selectedWarehouse?.value,
      );
      const response = await apiService.callAPI();

      if (runId !== exportRef.current) return;
      if (!response) throw new Error("Connection lost or API error.");

      const rawData = Array.isArray(response)
        ? response
        : response?.records || [];

      if (rawData.length === 0) {
        toast.dismiss(toastId);
        toast.error("No records found to export");
        return;
      }

      // Save to in-memory cache for subsequent clicks with same filter
      exportCacheRef.current = {
        filterKey: currentFilterKey,
        data: rawData,
      };

      exportDataToFile(rawData, toastId);
    } catch (error) {
      if (runId === exportRef.current) {
        toast.dismiss(toastId);
        toast.error("Export failed: " + (error as Error).message);
      }
    } finally {
      if (runId === exportRef.current) setIsExporting(false);
    }
  };

  return (
    <div className="p-5">
      <Row className="g-4">
        <Col>
          <div className="d-flex align-items-center mt-4">
            <h1 className="fs-22 fw-bolder m-0">{reportTitle}</h1>
            <button
              type="button"
              className="uw-btn uw-btn-light uw-btn-icon btn btn-icon btn-light ms-3"
              aria-label="Refresh report"
              title="Refresh report"
              disabled={fetchLoading || isExporting || reportFiltersPending}
              onClick={() => {
                responseCache.current.clear();
                exportCacheRef.current = null;
                void (isOutward ? handleApplyOutwardFilters() : handleApplyStandardFilters());
              }}
            >
              <i className="bi bi-arrow-clockwise" aria-hidden="true" />
            </button>
            {fetchLoading && (
              <span
                className="d-inline-flex align-items-center ms-4 fs-14 fw-semibold text-primary"
                role="status"
                aria-live="polite"
              >
                <span
                  className="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                ></span>
                Loading report data...
              </span>
            )}
          </div>
        </Col>
      </Row>

      {isOutward ? (
        <>
          <Row className="g-4 gx-lg-5 align-items-end mt-1">
            <Col xs={12} sm={6} md="auto">
              <FormLabel className="d-block fs-16 fw-500 mb-2 text-nowrap">
                From (EAT)
              </FormLabel>
              <input
                aria-label="Outward start date and time EAT"
                className="form-control outward-datetime-picker bg-white min-h-60px fs-16 fw-bold text-dark min-w-240px px-4"
                type="datetime-local"
                step={1}
                max={`${currentEastAfricaDate()}T23:59:59`}
                value={outwardStartAt}
                disabled={fetchLoading}
                onClick={(event) => {
                  try {
                    (event.currentTarget as any).showPicker?.();
                  } catch {
                    // ignore if showPicker is not supported
                  }
                }}
                onChange={(event) => void handleOutwardDateTimeChange("from", event.target.value)}
              />
            </Col>

            <Col xs={12} sm={6} md="auto">
              <FormLabel className="d-block fs-16 fw-500 mb-2 text-nowrap">
                Through (EAT)
              </FormLabel>
              <input
                aria-label="Outward end date and time EAT"
                className="form-control outward-datetime-picker bg-white min-h-60px fs-16 fw-bold text-dark min-w-240px px-4"
                type="datetime-local"
                step={1}
                max={`${currentEastAfricaDate()}T23:59:59`}
                value={outwardEndAt}
                disabled={fetchLoading}
                onClick={(event) => {
                  try {
                    (event.currentTarget as any).showPicker?.();
                  } catch {
                    // ignore if showPicker is not supported
                  }
                }}
                onChange={(event) => void handleOutwardDateTimeChange("to", event.target.value)}
              />
            </Col>

            <Col xs={12} sm={6} md="auto" style={{ width: "300px" }}>
              <FormLabel className="d-block fs-16 fw-500 mb-2 text-nowrap">
                Filter by Product
              </FormLabel>
              <div className="w-100">
                <CustomSelect
                  backgroundColor="#ffff"
                  isClearable={true}
                  minWidth="100%"
                  minHeight="60px"
                  controlFontSize="15px"
                  optionFontSize="14px"
                  options={productsList?.length ? productsList.map((val: any) => ({
                      label: (
                        <div className="d-flex align-items-center">
                          <div className="symbol symbol-30px border me-3">
                            <ImageWithFallback src={val?.media[0]?.url} className="object-fit-contain" alt="" noDefaultSize />
                          </div>
                          <span className="fs-16 fw-600 text-black mb-0">{val?.title}</span>
                        </div>
                      ),
                      value: val?.id,
                      id: val?.id,
                      variant: val,
                      title: val?.title,
                    })) : []}
                  value={selectedProduct}
                  onChange={(event: any) => handleProductSelect(event)}
                  onMenuClose={() => onMenuClose()}
                  disabled={productLoading || fetchLoading}
                  isLoading={fetchLoading}
                  hideSelectedOptions={false}
                />
              </div>
            </Col>

            <Col xs={12} sm={6} md="auto" style={{ width: "300px" }}>
              <FormLabel className="d-block fs-16 fw-500 mb-2 text-nowrap">
                Filter by Driver
              </FormLabel>
              <div className="w-100">
                <CustomSelect
                  backgroundColor="#ffff"
                  isClearable={true}
                  minWidth="100%"
                  minHeight="60px"
                  controlFontSize="15px"
                  optionFontSize="14px"
                  options={driversList.map((driver: any) => ({ label: driver.name, value: driver.id }))}
                  value={selectedDriver}
                  onChange={(event: any) => handleDriverSelect(event)}
                  disabled={fetchLoading || !driversList.length}
                  isLoading={fetchLoading}
                  hideSelectedOptions={false}
                />
              </div>
            </Col>

            <Col xs={12} md="auto" className="d-flex align-items-end gap-3 mt-2 mt-md-0 ms-md-auto">
              <button
                type="button"
                className="uw-btn uw-btn-primary btn btn-primary fs-15 fw-bold px-6 min-h-60px d-inline-flex align-items-center"
                onClick={() => void handleApplyOutwardFilters()}
                disabled={fetchLoading || !outwardStartAt || !outwardEndAt || !outwardRangeValid || !outwardFiltersPending}
                data-analytics-event="finance_reports_outward_apply_filters_clicked"
              >
                Apply Filters
              </button>
              {isAllowedToDownload && (
                <button
                  className="uw-btn uw-btn-primary btn btn-primary fs-15 fw-bold px-6 min-h-60px d-inline-flex align-items-center"
                  onClick={handleExportExcel}
                  disabled={fetchLoading || isExporting || reportFiltersPending}
                  data-analytics-event="finance_reports_finance_sales_report_download_clicked"
                >
                  <i className="bi bi-file-earmark-excel fs-4 me-2"></i>
                  {isExporting ? "Exporting..." : "Download Report"}
                </button>
              )}
            </Col>
          </Row>

          {!outwardRangeValid && (
            <div className="text-danger fs-13 mt-2" role="alert">
              The end time must be after the start time.
            </div>
          )}
          {reportFiltersPending && (
            <div className="text-warning fs-13 mt-2" role="status">
              Filter changes not applied — click Apply Filters to update results
            </div>
          )}
        </>
      ) : (
        <>
          <Row className="g-4 gx-lg-5 align-items-end mt-1">
            <Col xs={12} sm={6} md="auto">
              <FormLabel className="d-block fs-16 fw-500 mb-2 text-nowrap">
                Duration
              </FormLabel>
              <div className="min-w-md-288px">
                <CustomDatePicker
                  className="form-control bg-white min-h-60px fs-16 fw-bold text-dark min-w-md-288px min-w-175px cursor-pointer"
                  onChange={handleChange}
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  dateFormat="dd/MM/yyyy"
                  showFullMonthYearPicker={true}
                  maxDate={new Date()}
                  disabled={fetchLoading}
                  inputTextBG="bg-white"
                />
              </div>
            </Col>

            <Col xs={12} sm={6} md="auto" style={{ width: "300px" }}>
              <FormLabel className="d-block fs-16 fw-500 mb-2 text-nowrap">
                Filter by Product
              </FormLabel>
              <div className="w-100">
                <CustomSelect
                  backgroundColor="#ffff"
                  isClearable={true}
                  minWidth="100%"
                  minHeight="60px"
                  controlFontSize="15px"
                  optionFontSize="14px"
                  placeholder="Select Product"
                  options={productsList?.length ? productsList.map((val: any) => ({
                      label: (
                        <div className="d-flex align-items-center">
                          <div className="symbol symbol-30px border me-3">
                            <ImageWithFallback src={val?.media[0]?.url} className="object-fit-contain" alt="" noDefaultSize />
                          </div>
                          <span className="fs-16 fw-600 text-black mb-0">{val?.title}</span>
                        </div>
                      ),
                      value: val?.id,
                      id: val?.id,
                      variant: val,
                      title: val?.title,
                    })) : []}
                  value={selectedProduct}
                  onChange={(event: any) => handleProductSelect(event)}
                  onMenuClose={() => onMenuClose()}
                  disabled={productLoading || fetchLoading}
                  isDisabled={productLoading || fetchLoading}
                  isLoading={fetchLoading}
                  hideSelectedOptions={false}
                />
              </div>
            </Col>

            <Col xs={12} sm={6} md="auto" style={{ width: "300px" }}>
              <FormLabel className="d-block fs-16 fw-500 mb-2 text-nowrap">
                Filter by Driver
              </FormLabel>
              <div className="w-100">
                <CustomSelect
                  backgroundColor="#ffff"
                  isClearable={true}
                  minWidth="100%"
                  minHeight="60px"
                  controlFontSize="15px"
                  optionFontSize="14px"
                  placeholder="Select Driver"
                  options={driversList.map((driver: any) => ({ label: driver.name, value: driver.id }))}
                  value={selectedDriver}
                  onChange={(event: any) => handleDriverSelect(event)}
                  disabled={fetchLoading || !driversList.length}
                  isDisabled={fetchLoading || !driversList.length}
                  isLoading={fetchLoading}
                  hideSelectedOptions={false}
                />
              </div>
            </Col>

            <Col xs={12} md="auto" className="d-flex align-items-end gap-3 ms-md-auto">
              <button
                type="button"
                className="uw-btn uw-btn-primary btn btn-primary fs-15 fw-bold px-6 min-h-60px d-inline-flex align-items-center"
                onClick={() => void handleApplyStandardFilters()}
                disabled={fetchLoading || !standardDateRangeValid || !standardFiltersPending}
                data-analytics-event="finance_reports_finance_sales_apply_filters_clicked"
              >
                Apply Filters
              </button>
              {isAllowedToDownload && (
                <button
                  className="uw-btn uw-btn-primary btn btn-primary fs-15 fw-bold px-6 min-h-60px d-inline-flex align-items-center"
                  onClick={handleExportExcel}
                  disabled={fetchLoading || isExporting || reportFiltersPending}
                  data-analytics-event="finance_reports_finance_sales_report_download_clicked"
                >
                  <i className="bi bi-file-earmark-excel fs-4 me-2"></i>
                  {isExporting ? "Exporting..." : "Download Report"}
                </button>
              )}
            </Col>
          </Row>

          {reportFiltersPending && (
            <div className="text-warning fs-13 mt-2" role="status">
              Filter changes not applied — click Apply Filters to update results
            </div>
          )}
        </>
      )}

      {/* Summary Cards */}
      <Row className="mt-5 g-5">
        {hideDeliveredQty && (
          <Col xl={isOutward ? 12 : 4} lg={isOutward ? 12 : 6}>
            <Card className="border border-warning border-dashed bg-light-warning bg-opacity-25 shadow-sm h-100">
              <Card.Header className="min-h-50px px-6 py-3 border-bottom border-warning border-opacity-25">
                <div className="card-title m-0 d-flex align-items-center">
                  <span className="fs-15 fw-bolder text-warning text-uppercase">
                    Total Outward Summary
                  </span>
                </div>
              </Card.Header>
              <Card.Body className="d-flex justify-content-between flex-wrap align-items-center p-6 gap-3">
                <div className="d-flex flex-column flex-grow-1">
                  <span className="fs-13 fw-bold text-gray-500 mb-1">
                    INCL VAT
                  </span>
                  <span className="fs-22 fw-bolder text-dark" aria-label="Total Outward Summary INCL VAT">
                    {summaryAmount(totalLoadedInclVAT)}
                  </span>
                </div>
                <div className="d-flex flex-column flex-grow-1 border-start border-warning border-opacity-25 ps-5">
                  <span className="fs-13 fw-bold text-gray-500 mb-1">
                    EXCL VAT
                  </span>
                  <span className="fs-22 fw-bolder text-dark">
                    {summaryAmount(totalLoadedExclVAT)}
                  </span>
                </div>
                <div className="d-flex flex-column flex-grow-1 border-start border-warning border-opacity-25 ps-5">
                  <span className="fs-13 fw-bold text-gray-500 mb-1">
                    VAT AMOUNT
                  </span>
                  <span className="fs-22 fw-bolder text-warning">
                    {summaryAmount(totalLoadedVAT)}
                  </span>
                </div>
                {selectedProduct && (
                  <div className="d-flex flex-column flex-grow-1 border-start border-warning border-opacity-25 ps-5">
                    <span className="fs-13 fw-bold text-gray-500 mb-1">
                      {isOutward ? "TOTAL OUTWARD QTY" : "TOTAL QTY"}
                    </span>
                    <span className="fs-22 fw-bolder text-dark">
                      {summaryQty(loadedQtyByType)}
                    </span>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}

        {!hideDeliveredQty && (
          <Col xl={4} lg={6}>
            <Card className="border border-warning border-dashed bg-light-warning bg-opacity-25 shadow-sm h-100">
              <Card.Header className="min-h-50px px-6 py-3 border-bottom border-warning border-opacity-25">
                <div className="card-title m-0 d-flex align-items-center">
                  <span className="fs-15 fw-bolder text-warning text-uppercase">
                    Gross Sales
                  </span>
                </div>
              </Card.Header>
              <Card.Body className="d-flex justify-content-between flex-wrap align-items-center p-6 gap-3">
                <div className="d-flex flex-column flex-grow-1">
                  <span className="fs-13 fw-bold text-gray-500 mb-1">
                    INCL VAT
                  </span>
                  <span className="fs-22 fw-bolder text-dark" aria-label="Gross Sales INCL VAT">
                    {summaryAmount(totalLoadedInclVAT)}
                  </span>
                </div>
                <div className="d-flex flex-column flex-grow-1 border-start border-warning border-opacity-25 ps-5">
                  <span className="fs-13 fw-bold text-gray-500 mb-1">
                    EXCL VAT
                  </span>
                  <span className="fs-22 fw-bolder text-dark">
                    {summaryAmount(totalLoadedExclVAT)}
                  </span>
                </div>
                <div className="d-flex flex-column flex-grow-1 border-start border-warning border-opacity-25 ps-5">
                  <span className="fs-13 fw-bold text-gray-500 mb-1">
                    VAT AMOUNT
                  </span>
                  <span className="fs-22 fw-bolder text-warning">
                    {summaryAmount(totalLoadedVAT)}
                  </span>
                </div>
                {selectedProduct && (
                  <div className="d-flex flex-column flex-grow-1 border-start border-warning border-opacity-25 ps-5">
                    <span className="fs-13 fw-bold text-gray-500 mb-1">
                      TOTAL QTY
                    </span>
                    <span className="fs-22 fw-bolder text-dark">
                      {summaryQty(loadedQtyByType)}
                    </span>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}

        {!isOutward && !hideReturnedQty && (
          <Col xl={4} lg={6}>
            <Card className="border border-dashed bg-opacity-25 shadow-sm h-100 yellow-card">
              <Card.Header
                className="min-h-50px px-6 py-3 border-bottom"
                style={{ borderColor: "rgba(255, 204, 0, 0.25)" }}
              >
                <div className="card-title m-0 d-flex align-items-center">
                  <span
                    className="fs-15 fw-bolder text-uppercase"
                    style={{ color: "#FFCC00" }}
                  >
                    {isOutward ? "In-window Returns" : "Total Returned Summary"}
                  </span>
                </div>
              </Card.Header>
              <Card.Body className="d-flex justify-content-between flex-wrap align-items-center p-6 gap-3">
                <div className="d-flex flex-column flex-grow-1">
                  <span className="fs-13 fw-bold text-gray-500 mb-1">
                    INCL VAT
                  </span>
                  <span className="fs-22 fw-bolder text-dark" aria-label={`${isOutward ? "In-window Returns" : "Total Returned Summary"} INCL VAT`}>
                    {summaryAmount(totalReturnedInclVAT)}
                  </span>
                </div>
                <div
                  className="d-flex flex-column flex-grow-1 border-start ps-5"
                  style={{ borderColor: "rgba(255, 204, 0, 0.25)" }}
                >
                  <span className="fs-13 fw-bold text-gray-500 mb-1">
                    EXCL VAT
                  </span>
                  <span className="fs-22 fw-bolder text-dark">
                    {summaryAmount(totalReturnedExclVAT)}
                  </span>
                </div>
                <div
                  className="d-flex flex-column flex-grow-1 border-start ps-5"
                  style={{ borderColor: "rgba(255, 204, 0, 0.25)" }}
                >
                  <span className="fs-13 fw-bold text-gray-500 mb-1">
                    VAT AMOUNT
                  </span>
                  <span className="fs-22 fw-bolder" style={{ color: "#FFCC00" }}>
                    {summaryAmount(totalReturnedVAT)}
                  </span>
                </div>
                {selectedProduct && (
                  <div
                    className="d-flex flex-column flex-grow-1 border-start ps-5"
                    style={{ borderColor: "rgba(255, 204, 0, 0.25)" }}
                  >
                    <span className="fs-13 fw-bold text-gray-500 mb-1">
                      TOTAL QTY
                    </span>
                    <span className="fs-22 fw-bolder text-dark">
                      {summaryQty(returnedQtyByType)}
                    </span>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}

        {!isOutward && <Col xl={4} lg={6}>
          <Card
            className={clsx(
              "border border-dashed bg-opacity-25 shadow-sm h-100",
              netInclVAT >= 0
                ? "border-success bg-light-success"
                : "border-danger bg-light-danger",
            )}
          >
            <Card.Header
              className={clsx(
                "min-h-50px px-6 py-3 border-bottom",
                netInclVAT >= 0
                  ? "border-success border-opacity-25"
                  : "border-danger border-opacity-25",
              )}
            >
              <div className="card-title m-0 d-flex align-items-center">
                <span
                  className={clsx(
                    "fs-15 fw-bolder text-uppercase",
                    netInclVAT >= 0 ? "text-success" : "text-danger",
                  )}
                >
                  {isOutward
                    ? "Net After In-window Returns"
                    : "Net Sales (Gross Sales - Returned)"}
                </span>
              </div>
            </Card.Header>
            <Card.Body className="d-flex justify-content-between flex-wrap align-items-center p-6 gap-3">
              <div className="d-flex flex-column flex-grow-1">
                <span className="fs-13 fw-bold text-gray-500 mb-1">
                  INCL VAT
                </span>
                <span className="fs-22 fw-bolder text-dark" aria-label={`${isOutward ? "Net After In-window Returns" : "Net Sales (Gross Sales - Returned)"} INCL VAT`}>
                  {summaryAmount(netInclVAT)}
                </span>
              </div>
              <div
                className={clsx(
                  "d-flex flex-column flex-grow-1 border-start ps-5",
                  netInclVAT >= 0
                    ? "border-success border-opacity-25"
                    : "border-danger border-opacity-25",
                )}
              >
                <span className="fs-13 fw-bold text-gray-500 mb-1">
                  EXCL VAT
                </span>
                <span className="fs-22 fw-bolder text-dark">
                  {summaryAmount(netExclVAT)}
                </span>
              </div>
              <div
                className={clsx(
                  "d-flex flex-column flex-grow-1 border-start ps-5",
                  netInclVAT >= 0
                    ? "border-success border-opacity-25"
                    : "border-danger border-opacity-25",
                )}
              >
                <span className="fs-13 fw-bold text-gray-500 mb-1">
                  VAT AMOUNT
                </span>
                <span
                  className={clsx(
                    "fs-22 fw-bolder",
                    netInclVAT >= 0 ? "text-success" : "text-danger",
                  )}
                >
                  {summaryAmount(netVAT)}
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>}
      </Row>

      {/* Table Section */}
      <Card className="border border-r10px mt-6">
        <Card.Header className="px-6 py-4 border-bottom border-gray-200 d-flex align-items-center">
          <h5 className="m-0">{reportTitle}</h5>
          <h3 className="flex-grow-1 text-center m-0">
            {viewMode === "consolidated"
              ? "Product Specific View"
              : "Order Specific View"}
          </h3>
          <label className="switch-button ms-auto" htmlFor="viewModeToggle">
            <div className="switch-outer">
              <input
                id="viewModeToggle"
                type="checkbox"
                checked={viewMode === "consolidated"}
                onChange={(e) =>
                  setViewMode(e.target.checked ? "consolidated" : "normal")
                }
              />
              <div className="button">
                <span className="button-toggle"></span>
                <span className="button-indicator"></span>
              </div>
            </div>
          </label>
        </Card.Header>
        <Card.Body className="p-0">
          <InfiniteScroll
            hasMore={
              viewMode === "normal" &&
              !fetchLoading &&
              paymentData.length < totalRecords
            }
            loading={loadingMore}
            onLoadMore={() => void handleLoadMore()}
            rootMargin="0px 0px 400px 0px"
          >
          <div className="table-responsive">
            <table className="table table-row-bordered datatable align-middle gs-7 gy-4 my-0">
              <thead>
                {/* Visual Header Grouping */}
                <tr className="fs-13 fw-bolder text-uppercase bg-light text-gray-700 border-bottom border-gray-200">
                  <th className="min-w-250px p-4 border-end">
                    Product Summary
                  </th>
                  <th
                    colSpan={hideDeliveredQty ? (hideReturnedQty ? 2 : 3) : (hideReturnedQty ? 3 : 4)}
                    className="text-center p-4 bg-light-primary  border-opacity-25"
                  >
                    Supply Quantities
                  </th>
                  <th
                    colSpan={3}
                    className="text-center p-4 bg-light-info  border-opacity-25"
                  >
                    Unit Pricing
                  </th>
                  {hideDeliveredQty && (
                    <th
                      colSpan={3}
                      className="text-center p-4 border-end border-warning border-opacity-25"
                      style={{ backgroundColor: "#FFF5E6", color: "#FFCC00" }}
                    >
                      Outward Totals
                    </th>
                  )}
                  {!hideDeliveredQty && (
                    <th
                      colSpan={3}
                      className="text-center p-4 border-end border-primary border-opacity-25"
                      style={{ backgroundColor: "#F1FAFF", color: "#009EF7" }}
                    >
                      Gross Sales
                    </th>
                  )}
                  {!hideReturnedQty && !isOutward && (
                    <th
                      colSpan={3}
                      className="text-center p-4 border-end text-gray-700"
                      style={{
                        backgroundColor: "#FFFBE6",
                        color: "#FFCC00",
                        borderColor: "rgba(255, 204, 0, 0.25)",
                      }}
                    >
                      {isOutward ? "In-window Returned Totals" : "Returned Totals"}
                    </th>
                  )}
                  {!isOutward && <th
                    colSpan={3}
                    className={clsx(
                      "text-center p-4 rounded-end",
                      netInclVAT >= 0
                        ? " bg-light-success "
                        : "bg-light-danger ",
                    )}
                  >
                    {isOutward ? "Net After In-window Returns" : "Sales Totals"}
                  </th>}
                </tr>
                <tr className="fs-12 fw-bold h-50px align-middle text-muted bg-light bg-opacity-50">
                  <th className="min-w-250px border-end px-4">
                    PRODUCT NAME{" "}
                    {viewMode !== "consolidated" &&
                      "& ORDER ID - CUSTOMER NAME"}
                  </th>
                  <th className="min-w-100px text-center border-start border-primary border-opacity-25">
                    {hideDeliveredQty ? "ORDERED QTY" : "TOTAL QTY"}
                  </th>
                  <th className="min-w-100px text-center border-primary border-opacity-25">
                    {hideDeliveredQty ? "OUTWARD QTY" : "LOADED QTY"}
                  </th>
                  {!hideReturnedQty && (
                    <th className="min-w-100px text-center">{isOutward ? "IN-WINDOW RETURNED QTY" : "RETURNED QTY"}</th>
                  )}
                  {!hideDeliveredQty && (
                    <th className="min-w-100px text-center border-end border-primary border-opacity-25">DELIVERED QTY</th>
                  )}
                  <th className="min-w-125px text-end">INCL. VAT</th>
                  <th className="min-w-125px text-end">EXCL. VAT</th>
                  <th className="min-w-125px text-end border-end border-info border-opacity-25">
                    VAT / UNIT
                  </th>
                  {hideDeliveredQty && (
                    <>
                      <th className="min-w-150px text-end">TOTAL EXCL. VAT</th>
                      <th className="min-w-150px text-end border-end border-warning border-opacity-25 opacity-75">
                        VAT AMOUNT
                      </th>
                      <th className="min-w-150px text-end">TOTAL INCL. VAT</th>
                    </>
                  )}
                  {!hideDeliveredQty && (
                    <>
                      <th className="min-w-150px text-end">GROSS INCL. VAT</th>
                      <th className="min-w-150px text-end">GROSS EXCL. VAT</th>
                      <th className="min-w-150px text-end border-end border-primary border-opacity-25 opacity-75">GROSS VAT</th>
                    </>
                  )}
                  {!hideReturnedQty && (
                    <>
                      <th className="min-w-150px text-end">TOTAL INCL. VAT</th>
                      <th className="min-w-150px text-end">TOTAL EXCL. VAT</th>
                      <th className="min-w-150px text-end border-end border-danger border-opacity-25 opacity-75 pe-4">
                        VAT AMOUNT
                      </th>
                    </>
                  )}
                  {!isOutward && <th className="min-w-150px text-end">NET INCL. VAT</th>}
                  {!isOutward && <th className="min-w-150px text-end">NET EXCL. VAT</th>}
                  {!isOutward && <th className="min-w-150px text-end opacity-75 pe-4">
                    NET VAT
                  </th>}
                </tr>
              </thead>
              <tbody className="fs-14 fw-600">
                {fetchLoading ? (
                  <tr>
                    <td colSpan={exportHeaders.length - 2}>
                      <div className="d-flex justify-content-center py-5">
                        <Loader loading={fetchLoading} />
                      </div>
                    </td>
                  </tr>
                ) : (
                  <>
                    {displayData?.length ? (
                      sortedDisplayData.map((val: any, index: number) => {
                        const amounts = FinanceSalesAmounts(val);
                        const uom = getQuantityTypeLabel(val?.quantityType);
                        const totalQty = val?.totalQuantities || 0;
                        const loadedQty = val?.loadedQuantities ?? 0;
                        const returnedQty = val?.totalReturnedQuantities || 0;
                        const deliveredQty =
                          val?.deliveredQuantities !== undefined
                            ? val?.deliveredQuantities
                            : loadedQty - returnedQty;

                        const rowNetInclVAT = amounts.netIncl;
                        const rowNetExclVAT = amounts.netExcl;
                        const rowNetVAT = amounts.netVat;
                        const isPositive = rowNetInclVAT >= 0;

                        return (
                          <tr
                            key={index}
                            className="align-middle border-bottom border-gray-200 hover-bg-light transition"
                          >
                            {/* Product info */}
                            <td className="border-end px-4 py-3">
                              <div className="d-flex align-items-center">
                                <div className="symbol symbol-45px border me-3 bg-white rounded shadow-sm">
                                  <ImageWithFallback
                                    src={val?.variant?.media[0]?.url}
                                    className="object-fit-contain p-1 rounded"
                                    alt=""
                                    noDefaultSize
                                  />
                                </div>
                                <div className="d-flex flex-column text-nowrap">
                                  <span className="fs-14 fw-bolder text-dark mb-1">
                                    {val?.variant?.title?.replace(
                                      /\s*\)\s*/g,
                                      ")",
                                    )}
                                    {isOutward &&
                                      (val?.hasMovedToPending === true ||
                                        val?.routeVehicleStatus ===
                                          RouteOrderMovedToPendingOrder) && (
                                        <OverlayTrigger
                                          trigger={["hover", "focus"]}
                                          placement="top"
                                          overlay={
                                            <Popover className="bg-dark">
                                              <Popover.Body className="text-white fw-500 py-2 px-3">
                                                This route was moved to pending after dispatch.
                                              </Popover.Body>
                                            </Popover>
                                          }
                                        >
                                          <span
                                            className="ms-2 text-info"
                                            role="img"
                                            tabIndex={0}
                                            aria-label="Moved to pending after dispatch"
                                          >
                                            <i
                                              className="bi bi-info-circle-fill text-primary"
                                              aria-hidden="true"
                                            />
                                          </span>
                                        </OverlayTrigger>
                                      )}
                                  </span>
                                  {viewMode === "normal" && (
                                    <>
                                      <span className="fs-12 fw-600 text-muted">
                                        #{val?.refKey} - {val?.customerName}
                                      </span>
                                      <span className="fs-12 fw-500 text-muted mt-1">
                                        <i
                                          className="bi bi-truck me-1"
                                          aria-hidden="true"
                                          style={{ color: "#009EF7" }}
                                        />
                                        <span className="visually-hidden">
                                          Driver:
                                        </span>
                                        {val?.driverName || "N/A"}{" "}
                                        <span className="mx-2">|</span>
                                        <span className="visually-hidden">
                                          Order placed:
                                        </span>
                                        {val?.orderPlacedDate
                                          ? Method.convertDateToFormat(
                                              val?.orderPlacedDate,
                                              "DD/MM/YY [-] HH:mm",
                                            )
                                          : "N/A"}
                                      </span>
                                    </>
                                  )}
                                  {viewMode === "consolidated" && (
                                    <span className="fs-12 fw-600 text-muted">
                                      {val?.orderCount} route line
                                      {val?.orderCount > 1 ? "s" : ""}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Quantities */}
                            <td className="text-center font-monospace fw-600 border-start border-primary border-opacity-10 bg-light-primary bg-opacity-25">
                              {totalQty} <span className=" fs-12">{uom}</span>
                            </td>
                            <td className="text-center font-monospace fw-bolder text-danger bg-light-primary bg-opacity-25">
                              {loadedQty} <span className=" fs-12">{uom}</span>
                            </td>
                            {!hideReturnedQty && (
                              <td className="text-center font-monospace fw-bolder text-danger bg-light-primary bg-opacity-25">
                                {returnedQty}{" "}
                                <span className=" fs-12">{uom}</span>
                              </td>
                            )}
                            {!hideDeliveredQty && <td
                              className={clsx(
                                "text-center font-monospace fw-bolder border-end border-primary border-opacity-10 bg-light-primary bg-opacity-25",
                                hideDeliveredQty
                                  ? "text-dark"
                                  : "text-success",
                              )}
                            >
                              {deliveredQty}{" "}
                              <span className=" fs-12">{uom}</span>
                            </td>}

                            <td className="text-end fw-600 pe-3 text-nowrap">
                              <span className="text-muted fs-11 me-1 opacity-50">
                                TSh
                              </span>
                              {Method.getGeneralizedAmount(val?.averagePrice)}
                            </td>
                            <td className="text-end fw-600 pe-3 text-nowrap">
                              <span className="text-muted fs-11 me-1 opacity-50">
                                TSh
                              </span>
                              {Method.getGeneralizedAmount(
                                val?.averageTaxPrice,
                              )}
                            </td>
                            <td className="text-end fw-600 pe-3 border-end border-info border-opacity-10 text-nowrap bg-light-info bg-opacity-25">
                              <span className="text-info fs-11 me-1 opacity-75">
                                TSh{" "}
                                {Method.getGeneralizedAmount(val?.taxPerUnit)}
                              </span>
                            </td>
                            {/* Outward Totals (before Loaded when outward report) */}
                            {hideDeliveredQty && (
                              <>
                                <td className="text-end fw-bolder text-dark pe-3 text-nowrap border-start border-warning border-opacity-10">
                                  <span className="text-muted fs-11 me-1 fw-600 opacity-50">
                                    TSh
                                  </span>
                                  {Method.getGeneralizedAmount(
                                    amounts.loadedExcl,
                                  )}
                                </td>
                                <td className="text-end fw-bolder pe-3 text-nowrap" style={{ color: "#FF8C00" }}>
                                  <span className="text-muted fs-11 me-1 fw-600 opacity-50">
                                    TSh
                                  </span>
                                  {Method.getGeneralizedAmount(
                                    amounts.loadedVat,
                                  )}
                                </td>
                                <td className="text-end fw-bolder text-dark pe-3 text-nowrap">
                                  <span className="fs-11 me-1 fw-600 opacity-75">
                                    TSh
                                  </span>
                                  {Method.getGeneralizedAmount(amounts.loadedIncl)}
                                </td>
                              </>
                            )}

                            {!hideDeliveredQty && (
                              <>
                                <td className="text-end fw-bolder text-dark pe-3 text-nowrap border-start border-primary border-opacity-10">
                                  <span className="text-muted fs-11 me-1 fw-600 opacity-50">
                                    TSh
                                  </span>
                                  {Method.getGeneralizedAmount(amounts.loadedIncl)}
                                </td>
                                <td className="text-end fw-bolder text-dark pe-3 text-nowrap">
                                  <span className="text-muted fs-11 me-1 fw-600 opacity-50">
                                    TSh
                                  </span>
                                  {Method.getGeneralizedAmount(amounts.loadedExcl)}
                                </td>
                                <td
                                  className="text-end fw-bolder pe-3 border-end border-primary border-opacity-10 text-nowrap"
                                  style={{ color: "#009EF7" }}
                                >
                                  <span className="fs-11 me-1 fw-600 opacity-75">
                                    TSh
                                  </span>
                                  {Method.getGeneralizedAmount(amounts.loadedVat)}
                                </td>
                              </>
                            )}

                            {/* Returned Totals */}
                            {!hideReturnedQty && (
                              <>
                                <td className="text-end fw-bolder text-dark pe-3 text-nowrap border-start">
                                  <span className="text-muted fs-11 me-1 fw-600 opacity-50">
                                    TSh
                                  </span>
                                  {Method.getGeneralizedAmount(
                                    amounts.returnedIncl,
                                  )}
                                </td>
                                <td className="text-end fw-bolder text-dark pe-3 text-nowrap">
                                  <span className="text-muted fs-11 me-1 fw-600 opacity-50">
                                    TSh
                                  </span>
                                  {Method.getGeneralizedAmount(
                                    amounts.returnedExcl,
                                  )}
                                </td>
                                <td
                                  className="text-end fw-bolder pe-4 border-end text-nowrap"
                                  style={{ color: "#FFCC00" }}
                                >
                                  <span className="fs-11 me-1 fw-600 opacity-75">
                                    TSh
                                  </span>
                                  {Method.getGeneralizedAmount(
                                    amounts.returnedVat,
                                  )}
                                </td>
                              </>
                            )}

                            {/* Sales Totals */}
                            {!isOutward && <td className="text-end fw-bolder text-dark pe-3 text-nowrap border-start border-opacity-10">
                              <span className="text-muted fs-11 me-1 fw-600 opacity-50">
                                TSh
                              </span>
                              {Method.getGeneralizedAmount(rowNetInclVAT)}
                            </td>}
                            {!isOutward && <td className="text-end fw-bolder text-dark pe-3 text-nowrap">
                              <span className="text-muted fs-11 me-1 fw-600 opacity-50">
                                TSh
                              </span>
                              {Method.getGeneralizedAmount(rowNetExclVAT)}
                            </td>}
                            {!isOutward && <td
                              className={clsx(
                                "text-end fw-bolder pe-4 text-nowrap",
                                isPositive ? "text-success" : "text-danger",
                              )}
                            >
                              <span className="fs-11 me-1 fw-600 opacity-75">
                                TSh
                              </span>
                              {Method.getGeneralizedAmount(rowNetVAT)}
                            </td>}
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={exportHeaders.length - 2}>
                          <div className="w-100 fs-15 py-8 fw-bold d-flex justify-content-center text-muted">
                            No Data found
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
          </InfiniteScroll>
        </Card.Body>
      </Card>
    </div>
  );
};

export default FinanceSalesReport;